import { Stack } from '@mui/material';
import * as React from 'react';
import withToggle from '../../../HOCs/withToggle';
import { SYSTEM_PROP_ID } from '../../../sic-lib/defaults';
import { FieldValidationType } from '../../../sic-lib/enums/FieldValidationType';
import { GenericReducer, genericReducer } from '../../../sic-lib/reducers/genericReducer';
import { Contact } from '../../../sic-lib/types/Contact';
import { ContactField } from '../../../sic-lib/types/ContactField';
import { groupContactFieldsByValidationType } from '../../../sic-lib/utils/functionalReducers';
import ContactFieldCreate from './ContactFieldCreate';
import ContactFieldDisplay from './ContactFieldDisplay';
import ContactFieldEdit from './ContactFieldEdit';
import MenuAddContactField from './MenuAddContactField/MenuAddContactField';

const ContactFieldWithToggle = withToggle(ContactFieldDisplay, ContactFieldEdit);

interface ComponentState {
    unsavedField: ContactField | null;
}

interface Props {
    contact: Contact;
}

export default function ContactDetails({ contact }: Props) {
    const groupedFields = React.useMemo(
        () => groupContactFieldsByValidationType(contact.values),
        [contact.values],
    );

    const [compState, compDispatch] = React.useReducer<GenericReducer<ComponentState>>(
        genericReducer,
        {
            unsavedField: null,
        },
    );

    //~ should match the same type signature as "toggleEdit" passed into "ContactEditField"
    const handleClearNewField = () => compDispatch({ unsavedField: null });

    return (
        <Stack spacing={2}>
            {groupedFields.phone.map(fld => (
                <ContactFieldWithToggle
                    contact={contact}
                    contactField={fld}
                    key={`phn_${fld.fieldId}`}
                />
            ))}
            {compState.unsavedField !== null &&
                compState.unsavedField.validationType === FieldValidationType.usMobilePhone && (
                    <ContactFieldCreate
                        contactId={contact.contactId}
                        contactField={compState.unsavedField}
                        onToggleHandler={handleClearNewField}
                    />
                )}
            {groupedFields.email.map(fld => (
                <ContactFieldWithToggle
                    contact={contact}
                    contactField={fld}
                    key={`eml_${fld.fieldId}`}
                />
            ))}
            {compState.unsavedField !== null &&
                compState.unsavedField.validationType === FieldValidationType.email && (
                    <ContactFieldCreate
                        contactId={contact.contactId}
                        contactField={compState.unsavedField}
                        onToggleHandler={handleClearNewField}
                    />
                )}
            {groupedFields.address.map(fld => (
                <ContactFieldWithToggle
                    contact={contact}
                    contactField={fld}
                    key={`adr_${fld.fieldId}`}
                />
            ))}
            {compState.unsavedField !== null &&
                compState.unsavedField.validationType === FieldValidationType.address && (
                    <ContactFieldCreate
                        contactId={contact.contactId}
                        contactField={compState.unsavedField}
                        onToggleHandler={handleClearNewField}
                    />
                )}
            {groupedFields.date.map(fld => (
                <ContactFieldWithToggle
                    contact={contact}
                    contactField={fld}
                    key={`dte_${fld.fieldId}`}
                />
            ))}
            {compState.unsavedField !== null &&
                compState.unsavedField.validationType === FieldValidationType.date && (
                    <ContactFieldCreate
                        contactId={contact.contactId}
                        contactField={compState.unsavedField}
                        onToggleHandler={handleClearNewField}
                    />
                )}
            {groupedFields.dollar.map(fld => (
                <ContactFieldWithToggle
                    contact={contact}
                    contactField={fld}
                    key={`dlr_${fld.fieldId}`}
                />
            ))}
            {compState.unsavedField !== null &&
                compState.unsavedField.validationType === FieldValidationType.dollars && (
                    <ContactFieldCreate
                        contactId={contact.contactId}
                        contactField={compState.unsavedField}
                        onToggleHandler={handleClearNewField}
                    />
                )}
            {groupedFields.year.map(fld => (
                <ContactFieldWithToggle
                    contact={contact}
                    contactField={fld}
                    key={`yer_${fld.fieldId}`}
                />
            ))}
            {compState.unsavedField !== null &&
                compState.unsavedField.validationType === FieldValidationType.years && (
                    <ContactFieldCreate
                        contactId={contact.contactId}
                        contactField={compState.unsavedField}
                        onToggleHandler={handleClearNewField}
                    />
                )}
            {groupedFields.other.map(
                fld =>
                    (fld.fieldId === 3 || fld.fieldId > SYSTEM_PROP_ID) && (
                        <ContactFieldWithToggle
                            contact={contact}
                            contactField={fld}
                            key={`oth_${fld.fieldId}`}
                        />
                    ),
            )}
            {compState.unsavedField !== null &&
                compState.unsavedField.validationType === FieldValidationType.other && (
                    <ContactFieldCreate
                        contactId={contact.contactId}
                        contactField={compState.unsavedField}
                        onToggleHandler={handleClearNewField}
                    />
                )}
            <Stack direction="row" justifyContent="flex-end">
                <MenuAddContactField contact={contact} onSelectHandler={compDispatch} />
            </Stack>
        </Stack>
    );
}
